import { useEffect, useRef, useState, type ReactNode } from "react";
import { displayName } from "../../../../engine/selectors";
import type { Pair, Score, TournamentState } from "../../../../engine/types";
import type { Tournament } from "../../lib/useTournament";

/** What every tab gets: the hook, the state (never null by the time a tab renders), and a toast. */
export interface TabProps {
  t: Tournament;
  state: TournamentState;
  say: (text: string) => void;
  /** Who is holding this phone (null until they have said), and the way to say or change it. */
  me: {
    name: string | null;
    set: (name: string) => void;
    /** A named admin's link (decision 38): the name comes with it and cannot be changed here. */
    named: boolean;
  };
}

/** Re-render on a timer, for countdowns. The time itself always comes from t.now(). */
export function useTick(ms = 250): void {
  const [, setN] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setN((n) => n + 1), ms);
    return () => clearInterval(id);
  }, [ms]);
}

export function mmss(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Time left in the current phase, frozen while everything is paused. Null between rounds. */
/**
 * Between rounds as the hall sees it: no game on. With rounds started by an admin (decision 41)
 * the changeover counts down to nothing, so it is between rounds too, as the wall and the tablets
 * say, and Start can go at once.
 */
export function betweenRounds(state: TournamentState): boolean {
  return state.clock.phase === "idle" || (state.clock.phase === "changeover" && !state.config.autoAdvance);
}

/** Time left on the clock, or null when nothing is counting (between rounds). */
export function remaining(state: TournamentState, now: number): number | null {
  const { phaseEndsAt, pausedAt } = state.clock;
  if (phaseEndsAt === null || (state.clock.phase === "changeover" && betweenRounds(state))) return null;
  return phaseEndsAt - (state.pausedAll && pausedAt !== null ? pausedAt : now);
}

export function phaseLabel(state: TournamentState): string {
  if (state.clock.phase === "playing") return "Playing";
  if (!betweenRounds(state)) return "Changeover";
  return "Between rounds";
}

export function nameOf(state: TournamentState, id: string): string {
  return displayName(state.players[id], "admin");
}

export function pairNames(state: TournamentState, pair: Pair): string {
  return `${nameOf(state, pair[0])} & ${nameOf(state, pair[1])}`;
}

export function eventName(state: TournamentState, id: string | null): string | null {
  if (!id) return null;
  return state.config.eventTypes.find((e) => e.id === id)?.name ?? id;
}

export function scoreText(score: Score): string {
  return `${score.a}-${score.b}${score.capped ? " (time)" : ""}`;
}

export function clockTime(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export function CourtBadge({ court }: { court: number }) {
  const colour = ((court - 1) % 4) + 1;
  return (
    <span className="ad-court" style={{ background: `var(--bd-court-${colour})` }}>
      {court}
    </span>
  );
}

/** A plain on/off switch: a 48 px button that says what it is and whether it is on. */
export function Toggle({ label, on, onChange, disabled }: { label: string; on: boolean; onChange: (on: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      className={"ad-toggle" + (on ? " on" : "")}
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={() => onChange(!on)}
    >
      <span className="ad-toggle-label">{label}</span>
      <span className="ad-toggle-state">{on ? "On" : "Off"}</span>
    </button>
  );
}

export function Stepper({ label, value, onChange, min = 0, step = 1, format }: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  step?: number;
  format?: (v: number) => string;
}) {
  return (
    <div className="ad-stepper">
      <div className="ad-stepper-label">{label}</div>
      <div className="ad-stepper-row">
        <button type="button" className="ad-step" aria-label={`${label} down`} disabled={value - step < min - 1e-9}
          onClick={() => onChange(Math.round((value - step) * 1000) / 1000)}>
          -
        </button>
        <span className="ad-stepper-value">{format ? format(value) : value}</span>
        <button type="button" className="ad-step" aria-label={`${label} up`}
          onClick={() => onChange(Math.round((value + step) * 1000) / 1000)}>
          +
        </button>
      </div>
    </div>
  );
}

/** Sent up the page by something that has just opened keys of its own, so the undo bar and the toast get out of its way. */
export const OPENED_EVENT = "bd-admin-opened";

/** Tell the screen that this part has just opened: put the ref on it, and pass whether it is open. */
export function useOpened<T extends HTMLElement>(open: boolean) {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    if (open) ref.current?.dispatchEvent(new CustomEvent(OPENED_EVENT, { bubbles: true }));
  }, [open]);
  return ref;
}

/** A button that asks "Sure?" in place before it acts. */
export function ConfirmButton({ label, question, yes = "Yes", onConfirm, className = "btn quiet", disabled }: {
  label: ReactNode;
  question: string;
  yes?: string;
  onConfirm: () => void;
  className?: string;
  disabled?: boolean;
}) {
  const [asking, setAsking] = useState(false);
  const box = useOpened<HTMLDivElement>(asking);
  if (!asking) {
    return (
      <button type="button" className={className} disabled={disabled} onClick={() => setAsking(true)}>
        {label}
      </button>
    );
  }
  return (
    <div className="ad-confirm" ref={box}>
      <p>{question}</p>
      <div className="ad-row">
        <button type="button" className="btn warn" onClick={() => { setAsking(false); onConfirm(); }}>
          {yes}
        </button>
        <button type="button" className="btn quiet" onClick={() => setAsking(false)}>
          Cancel
        </button>
      </div>
    </div>
  );
}
